import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Building2, Wallet, Plus, ChevronLeft, Check, Loader2 } from 'lucide-react';

const PRESET_AMOUNTS = [25, 50, 100, 250, 500];
const TOKEN_PRICE = 0.05; // $0.05 per RLY token

export default function TokenPurchaseModal({ isOpen, onClose, userId, existingWallet }) {
  const [step, setStep] = useState('amount'); // amount, wallet, payment, processing, complete
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [walletOption, setWalletOption] = useState(existingWallet ? 'existing' : 'new');
  const [walletAddress, setWalletAddress] = useState(existingWallet || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseData, setPurchaseData] = useState(null);

  const tokenAmount = Math.floor((customAmount ? parseFloat(customAmount) : amount) / TOKEN_PRICE);
  const displayAmount = customAmount ? parseFloat(customAmount) : amount;

  useEffect(() => {
    if (isOpen) {
      setStep('amount');
      setAmount(50);
      setCustomAmount('');
      setWalletOption(existingWallet ? 'existing' : 'new');
      setWalletAddress(existingWallet || '');
    }
  }, [isOpen, existingWallet]);

  const isValidWalletAddress = (address) => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  const handleAmountSelect = (preset) => {
    setAmount(preset);
    setCustomAmount('');
  };

  const handleContinueFromAmount = () => {
    if (displayAmount >= 10) {
      setStep('wallet');
    }
  };

  const handleContinueFromWallet = () => {
    if (walletOption === 'new' || isValidWalletAddress(walletAddress)) {
      setStep('payment');
    }
  };

  const handlePaymentSelect = async (method) => {
    setIsProcessing(true);
    setStep('processing');

    // Simulate creating purchase and getting checkout URL
    // In production, this would call your backend API
    setTimeout(() => {
      setPurchaseData({
        orderId: `ORD-${Date.now()}`,
        tokenAmount,
        amount: displayAmount,
        walletAddress: walletOption === 'new' ? '0x742d35Cc...0bEb' : walletAddress,
        method
      });

      // Simulate payment completion after 3 seconds
      setTimeout(() => {
        setStep('complete');
        setIsProcessing(false);
      }, 3000);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#1F2937] rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              {step !== 'amount' && step !== 'complete' && (
                <button
                  onClick={() => {
                    if (step === 'wallet') setStep('amount');
                    if (step === 'payment') setStep('wallet');
                  }}
                  className="p-1 hover:bg-white/10 rounded-lg"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-400" />
                </button>
              )}
              <h2 className="text-lg font-bold text-white">
                {step === 'amount' && 'Buy Rally Tokens'}
                {step === 'wallet' && 'Wallet Setup'}
                {step === 'payment' && 'Payment Method'}
                {step === 'processing' && 'Processing...'}
                {step === 'complete' && 'Purchase Complete!'}
              </h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Amount Step */}
            {step === 'amount' && (
              <div className="space-y-6">
                <p className="text-gray-400 text-sm">Select amount to purchase</p>

                {/* Preset Amounts */}
                <div className="grid grid-cols-3 gap-3">
                  {PRESET_AMOUNTS.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => handleAmountSelect(preset)}
                      className={`p-4 rounded-xl text-lg font-semibold transition-all ${
                        amount === preset && !customAmount
                          ? 'bg-[#8B5CF6] text-white border-2 border-[#A78BFA]'
                          : 'bg-[#374151] text-white border-2 border-transparent hover:bg-[#4B5563]'
                      }`}
                    >
                      ${preset}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setAmount(0);
                      setCustomAmount('');
                    }}
                    className={`p-4 rounded-xl text-lg font-semibold transition-all ${
                      customAmount
                        ? 'bg-[#8B5CF6] text-white border-2 border-[#A78BFA]'
                        : 'bg-[#374151] text-white border-2 border-transparent hover:bg-[#4B5563]'
                    }`}
                  >
                    Custom
                  </button>
                </div>

                {/* Custom Amount Input */}
                <div>
                  <label className="text-gray-400 text-sm block mb-2">Or enter custom amount:</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">$</span>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="0.00"
                      min="10"
                      max="10000"
                      className="w-full pl-8 pr-16 py-4 bg-[#374151] border border-[#4B5563] rounded-lg text-white text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">USD</span>
                  </div>
                </div>

                {/* Token Estimate */}
                <div className="p-4 bg-[#8B5CF6]/10 rounded-xl text-center">
                  <span className="text-[#A78BFA]">
                    {tokenAmount.toLocaleString()} RLY tokens @ ${TOKEN_PRICE}/token
                  </span>
                </div>

                <button
                  onClick={handleContinueFromAmount}
                  disabled={displayAmount < 10}
                  className={`w-full py-4 rounded-lg font-semibold transition-colors ${
                    displayAmount >= 10
                      ? 'bg-[#8B5CF6] hover:bg-[#7C3AED] text-white'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue to Payment
                </button>
              </div>
            )}

            {/* Wallet Step */}
            {step === 'wallet' && (
              <div className="space-y-6">
                <p className="text-gray-400 text-sm">Where should we send your tokens?</p>

                <div className="space-y-3">
                  <label
                    onClick={() => setWalletOption('new')}
                    className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                      walletOption === 'new'
                        ? 'bg-[#8B5CF6]/10 border-2 border-[#8B5CF6]'
                        : 'bg-[#374151] border-2 border-transparent hover:bg-[#4B5563]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="walletOption"
                      value="new"
                      checked={walletOption === 'new'}
                      onChange={() => setWalletOption('new')}
                      className="mt-1 w-4 h-4 accent-[#8B5CF6]"
                    />
                    <div>
                      <p className="font-semibold text-white">Create new wallet for me</p>
                      <p className="text-sm text-gray-400">We'll generate a secure wallet automatically</p>
                    </div>
                  </label>

                  <label
                    onClick={() => setWalletOption('existing')}
                    className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                      walletOption === 'existing'
                        ? 'bg-[#8B5CF6]/10 border-2 border-[#8B5CF6]'
                        : 'bg-[#374151] border-2 border-transparent hover:bg-[#4B5563]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="walletOption"
                      value="existing"
                      checked={walletOption === 'existing'}
                      onChange={() => setWalletOption('existing')}
                      className="mt-1 w-4 h-4 accent-[#8B5CF6]"
                    />
                    <div>
                      <p className="font-semibold text-white">Use my existing wallet</p>
                      <p className="text-sm text-gray-400">Enter your Rally wallet address</p>
                    </div>
                  </label>
                </div>

                {walletOption === 'existing' && (
                  <div>
                    <label className="text-gray-400 text-sm block mb-2">Wallet Address</label>
                    <input
                      value={walletAddress}
                      onChange={(e) => setWalletAddress(e.target.value)}
                      placeholder="0x..."
                      className="w-full px-4 py-3 bg-[#374151] border border-[#4B5563] rounded-lg text-white font-mono focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                    />
                    {walletAddress && !isValidWalletAddress(walletAddress) && (
                      <p className="text-red-400 text-xs mt-1">Please enter a valid Ethereum address</p>
                    )}
                    <p className="text-gray-500 text-xs mt-2">
                      Your wallet address will be saved for future purchases
                    </p>
                  </div>
                )}

                <button
                  onClick={handleContinueFromWallet}
                  disabled={walletOption === 'existing' && !isValidWalletAddress(walletAddress)}
                  className={`w-full py-4 rounded-lg font-semibold transition-colors ${
                    walletOption === 'new' || isValidWalletAddress(walletAddress)
                      ? 'bg-[#8B5CF6] hover:bg-[#7C3AED] text-white'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue
                </button>
              </div>
            )}

            {/* Payment Step */}
            {step === 'payment' && (
              <div className="space-y-6">
                {/* Purchase Summary */}
                <div className="p-4 bg-[#374151] rounded-xl space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Purchase:</span>
                    <span className="text-white font-semibold">${displayAmount.toFixed(2)} USD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">You'll receive:</span>
                    <span className="text-[#8B5CF6] font-semibold">{tokenAmount.toLocaleString()} RLY</span>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="space-y-3">
                  <button
                    onClick={() => handlePaymentSelect('card')}
                    className="w-full flex items-center gap-4 p-4 bg-[#374151] hover:bg-[#4B5563] border-2 border-[#4B5563] hover:border-[#8B5CF6] rounded-xl transition-all"
                  >
                    <div className="w-12 h-12 bg-[#4B5563] rounded-xl flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-white">Credit/Debit Card</p>
                      <p className="text-sm text-gray-400">Instant processing</p>
                    </div>
                    <span className="text-[#8B5CF6] text-xl">&rarr;</span>
                  </button>

                  <button
                    onClick={() => handlePaymentSelect('ach')}
                    className="w-full flex items-center gap-4 p-4 bg-[#374151] hover:bg-[#4B5563] border-2 border-[#4B5563] hover:border-[#8B5CF6] rounded-xl transition-all"
                  >
                    <div className="w-12 h-12 bg-[#4B5563] rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-white">Bank Transfer (ACH)</p>
                      <p className="text-sm text-gray-400">2-3 business days</p>
                    </div>
                    <span className="text-[#8B5CF6] text-xl">&rarr;</span>
                  </button>
                </div>

                <div className="text-center text-sm text-gray-500">
                  Secure payment powered by Switchrewardcard
                </div>
              </div>
            )}

            {/* Processing Step */}
            {step === 'processing' && (
              <div className="py-12 text-center space-y-6">
                <div className="w-16 h-16 mx-auto bg-[#8B5CF6]/20 rounded-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-[#8B5CF6] animate-spin" />
                </div>
                <div>
                  <p className="text-white font-semibold text-lg">Processing Your Purchase...</p>
                  <p className="text-gray-400 text-sm mt-2">This may take a few moments.<br />Please don't close this window.</p>
                </div>
              </div>
            )}

            {/* Complete Step */}
            {step === 'complete' && purchaseData && (
              <div className="py-6 text-center space-y-6">
                <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-500" />
                </div>

                <div className="p-6 bg-[#374151] rounded-xl space-y-4">
                  <p className="text-gray-400 text-sm">You purchased:</p>
                  <p className="text-[#8B5CF6] text-3xl font-bold">
                    {purchaseData.tokenAmount.toLocaleString()} RLY
                  </p>
                  <p className="text-white">for ${purchaseData.amount.toFixed(2)}</p>

                  <div className="pt-4 border-t border-white/10">
                    <p className="text-gray-400 text-sm">Tokens are being sent to:</p>
                    <code className="inline-block mt-2 px-3 py-2 bg-[#1F2937] rounded-lg text-[#A78BFA] text-sm font-mono">
                      {purchaseData.walletAddress.slice(0, 6)}...{purchaseData.walletAddress.slice(-4)}
                    </code>
                  </div>

                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <p className="text-blue-400 text-sm">Estimated arrival: 2-5 minutes</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-3 border border-white/20 text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-lg transition-colors"
                  >
                    <Wallet className="w-4 h-4" />
                    View Wallet
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
